import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import TableFilter from "src/components/TableFilter";
import CommonService from "src/modules/data-protection/apis/CommonService";
import ControlService from "src/modules/data-protection/apis/ControlService";

const FilteringComponent = ({
    t,
    lng,
    onChange
}) => {
    const [ tableFilters, setTableFilters ] = useState({
        text: undefined,
        taskStatus: undefined,
        sectionId: undefined,
        hasTasks: undefined,
        hasOpenTasks: undefined,
        hasOverdueTasks: undefined,
        hasDocuments: undefined,
        applicable: undefined,
        legalEntityIds: undefined,
    });

    const {
        data: sections,
        isFetching: sectionsAreFetching
    } = useQuery({
        queryKey: [
            "data-protection-controls-sections-list",
            lng
        ],
        queryFn: async () => {
            const service = ControlService.getInstance();
            return await service.fetchSections();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("Failed to load the list of control sections."), {
                type: "error"
            });
        }
    });

    const { data: legalEntities, isFetching: entitiesAreFetching } = useQuery({
        queryKey: ["data-protection-fetch-all-legal-entities-list", lng],
        queryFn: async () => {
          const service = CommonService.getInstance();
          return await service.fetchAllLegalEntities();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
          toast(t("An error occurred while fetching entities."), {
            type: "error",
          });
        },
      });

      const statuses = useMemo(() => {
        return [
            { value: "new", label: t("New") },
            { value: "in_progress", label: t("In Progress") },
            { value: "review", label: t("Review") },
            { value: "closed", label: t("Closed") },
            { value: "deleted", label: t("Deleted") },
        ];
    }, [t]);

    const tableFilterTabs = useMemo(() => {
        return [
            {
                type: 'check_list',
                name: 'taskStatus',
                title: 'Task Status',
                icon: <i className="ri-checkbox-circle-line" />,
                isLoading: false,
                defaultValue: tableFilters?.taskStatus,
                data: (statuses || []).map((item) => ({
                    ...item,
                    id: item.value
                })),
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.label) 
                }
            },
            {
                type: 'radio_list',
                name: 'sectionId',
                title: 'Section',
                icon: <i className="ri-list-unordered" />,
                isLoading: sectionsAreFetching,
                defaultValue: tableFilters?.sectionId,
                data: sections || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'radio_list',
                name: 'hasTasks',
                title: 'Has Tasks',
                icon: <i className="ri-checkbox-circle-line" />,
                isLoading: false,
                defaultValue: tableFilters?.hasTasks,
                data: [
                    {
                        id: true,
                        title: 'Yes'
                    },
                    {
                        id: false,
                        title: 'No'
                    }
                ],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'radio_list',
                name: 'hasOpenTasks',
                title: 'Has Open Tasks',
                icon: <i className="ri-checkbox-circle-line" />,
                isLoading: false,
                defaultValue: tableFilters?.hasOpenTasks,
                data: [
                    {
                        id: 'true',
                        title: 'Yes'
                    },
                    {
                        id: 'false',
                        title: 'No'
                    }
                ],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'radio_list',
                name: 'hasOverdueTasks',
                title: 'Has Overdue Tasks',
                icon: <i className="ri-alarm-warning-line" />,
                isLoading: false,
                defaultValue: tableFilters?.hasOverdueTasks,
                data: [
                    {
                        id: 'true',
                        title: 'Yes'
                    },
                    {
                        id: 'false',
                        title: 'No'
                    }
                ],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'radio_list',
                name: 'hasDocuments',
                title: 'Has Documents',
                icon: <i className="ri-file-list-3-line" />,
                isLoading: false,
                defaultValue: tableFilters?.hasDocuments,
                data: [
                    {
                        id: 'true',
                        title: 'Yes'
                    },
                    {
                        id: 'false',
                        title: 'No'
                    }
                ],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'radio_list',
                name: 'applicable',
                title: 'Applicable',
                icon: <i className="ri-checkbox-line" />,
                isLoading: false,
                defaultValue: tableFilters?.applicable,
                data: [
                    {
                        id: 'true',
                        title: 'Yes'
                    },
                    {
                        id: 'false',
                        title: 'No'
                    }
                ],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: "check_list",
                name: "legalEntityIds",
                title: "Connected Legal Entities",
                icon: <i className="ri-git-branch-line" />,
                isLoading: entitiesAreFetching,
                defaultValue: tableFilters?.legalEntityIds,
                data: legalEntities?.legalEntities || [],
                searchInListOption: {
                  enabled: true,
                  remote: false,
                },
                formatter: (item) => {
                  return item.title;
                },
              },
            {
                type: 'search_textbox',
                name: 'text',
                title: 'Search by title or section',
                icon: <i className="ri-terminal-box-line" />,
                defaultValue: tableFilters?.text,
                options: {
                    placeholder: t('Search by title or section'),
                    typingDelay: 750
                },
                displayIndependently: true,
                onFilterChange: (value) => {
                    setTableFilters((currentState) => ({...currentState, text: value}))
                }
            }
        ]
    }, [
        tableFilters,
        statuses,
        sectionsAreFetching,
        entitiesAreFetching,
        sections,
        t
    ]);

    const onTableFilterOkButtonClicked = (filters, independentTabsData) => {
        if (filters) {
            const result = {}
            if ('taskStatus' in filters) {
                result.taskStatus = filters.taskStatus !== 'all' ? filters.taskStatus : undefined
            }

            if ('sectionId' in filters) {
                result.sectionId = filters.sectionId !== 'all' ? filters.sectionId : undefined
            }

            if ('hasTasks' in filters) {
                result.hasTasks = filters.hasTasks !== 'all' ? filters.hasTasks : undefined
            }

            if ('hasOpenTasks' in filters) {
                result.hasOpenTasks = filters.hasOpenTasks !== 'all' ? filters.hasOpenTasks : undefined
            }

            if ('hasOverdueTasks' in filters) {
                result.hasOverdueTasks = filters.hasOverdueTasks !== 'all' ? filters.hasOverdueTasks : undefined
            }

            if ('hasDocuments' in filters) {
                result.hasDocuments = filters.hasDocuments !== 'all' ? filters.hasDocuments : undefined
            }

            if ('applicable' in filters) {
                result.applicable = filters.applicable !== 'all' ? filters.applicable : undefined
            }

            if ("legalEntityIds" in filters) {
                result.legalEntityIds =
                  filters.legalEntityIds !== "all"
                    ? filters.legalEntityIds
                    : undefined;
              }

            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                text: independentTabsData?.text,
                taskStatus: undefined,
                sectionId: undefined,
                hasTasks: undefined,
                hasOpenTasks: undefined,
                hasOverdueTasks: undefined,
                hasDocuments: undefined,
                applicable: undefined,
                legalEntityIds: undefined,
            })
        }
    }

    useEffect(() => {
        onChange && onChange(tableFilters)
    }, [ 
        tableFilters,
        onChange
    ]);

    return (
        <TableFilter
            className="me-2 controls-filter"
            defaultActiveTab='status'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

export default withNamespaces()(FilteringComponent);